import { Card, CardContent, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import IncDataGrid from 'components/inc-data-grid/inc-data-grid.component';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RetentionVisitViewRow } from './retention.utils';

export type RetentionDetailTableProps = {
  visits?: RetentionVisitViewRow[];
  'data-testid'?: string;
};

const fmtPct = (value: number | null) =>
  value == null ? '—' : `${Math.round(value * 100)}%`;
const fmtDays = (value: number | null) => (value == null ? '—' : `${value}d`);
const fmtCount = (value: number | null) =>
  value == null ? '—' : value.toLocaleString();

type MetricRow = {
  id: string;
  getLabel: (t: TFunction) => string;
  getValue: (row: RetentionVisitViewRow) => string;
  /** Highlight this row's cells for critical visits (matches the chart). */
  highlightCritical?: boolean;
};

const METRIC_ROWS: MetricRow[] = [
  {
    id: 'conversionFromLast',
    getLabel: t =>
      t('page.guestJourney.retention.detailTable.rows.conversionFromLast'),
    getValue: row => fmtPct(row.conversionFromLastVisitPct),
    highlightCritical: true,
  },
  {
    id: 'conversionFromSignup',
    getLabel: t =>
      t('page.guestJourney.retention.detailTable.rows.conversionFromSignup'),
    getValue: row => fmtPct(row.conversionFromSignup),
  },
  {
    id: 'medianDays',
    getLabel: t => t('page.guestJourney.retention.detailTable.rows.medianDays'),
    getValue: row => fmtDays(row.medianDaysFromLastVisit),
  },
  {
    id: 'meanDays',
    getLabel: t => t('page.guestJourney.retention.detailTable.rows.meanDays'),
    getValue: row => fmtDays(row.meanDaysFromLastVisit),
  },
  {
    id: 'customers',
    getLabel: t => t('page.guestJourney.retention.detailTable.rows.customers'),
    getValue: row => fmtCount(row.customerCount),
  },
];

const CRITICAL_CLASS = 'RetentionDetailTable-critical';

/**
 * Grid row shape: one row per metric, one dynamic column per visit number,
 * matching the transposed layout of the design (visits across the top, in
 * line with the chart's x-axis).
 */
type DetailGridRow = {
  id: string;
  metric: string;
  highlightCritical: boolean;
  [visitNumber: string]: string | boolean;
};

const RetentionDetailTable = ({
  visits = [],
  'data-testid': dataTestId = 'Retention.DetailTable',
}: RetentionDetailTableProps) => {
  const { t } = useTranslation();

  const columns: GridColDef<DetailGridRow>[] = useMemo(
    () => [
      {
        field: 'metric',
        headerName: t('page.guestJourney.retention.detailTable.visitHeader'),
        flex: 2,
        minWidth: 220,
        sortable: false,
      },
      ...visits.map<GridColDef<DetailGridRow>>(visit => ({
        field: String(visit.visitNumber),
        headerName: String(visit.visitNumber),
        flex: 1,
        minWidth: 72,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        headerClassName: visit.critical ? CRITICAL_CLASS : '',
        cellClassName: params =>
          visit.critical && params.row.highlightCritical ? CRITICAL_CLASS : '',
      })),
    ],
    [t, visits],
  );

  const rows: DetailGridRow[] = useMemo(
    () =>
      METRIC_ROWS.map(metric => ({
        id: metric.id,
        metric: metric.getLabel(t),
        highlightCritical: !!metric.highlightCritical,
        ...Object.fromEntries(
          visits.map(visit => [
            String(visit.visitNumber),
            metric.getValue(visit),
          ]),
        ),
      })),
    [t, visits],
  );

  return (
    <Card data-testid={dataTestId}>
      <CardContent>
        <Typography variant="body2" component="div" marginBottom={1}>
          {t('page.guestJourney.retention.detailTable.title')}
        </Typography>

        <IncDataGrid
          data-testid={`${dataTestId}.Grid`}
          columns={columns}
          rows={rows}
          autoHeight
          hideFooter
          disableColumnMenu
          disableColumnReorder
          disableRowSelectionOnClick
          sx={{
            // The grid sits inside a white Card; the default header background
            // (background.default) reads as a gray band there.
            '--DataGrid-containerBackground': 'transparent',
            [`& .${CRITICAL_CLASS}`]: {
              color: 'error.main',
              fontWeight: 700,
            },
          }}
        />
      </CardContent>
    </Card>
  );
};

export default RetentionDetailTable;
